import { Injectable } from "@angular/core";
import { DeathHistoryRecord } from "models/assessment/death-history-record";
import { DeathInformation } from "models/assessment/death-information";
import { HistoryRecord } from "models/assessment/history-record";
import { ResourceManagementService } from "providers/resource-management/resource-management.service";
import { Observable } from "rxjs";
import { forkJoin } from "rxjs/observable/forkJoin";
import { of } from "rxjs/observable/of";
import { catchError, map, timeout } from "rxjs/operators";
import { getDeathVisitLabel } from "./death-visit-labels";

@Injectable()
export class DeathVisitHistoryService {
  private readonly PROVIDER_FETCH_TIMEOUT_MS = 5000;
  private readonly UNKNOWN_PROVIDER_LABEL = "Unknown Provider";

  constructor(private resourceManagementService: ResourceManagementService) {}

  /**
   * Processes death visit history records for display in the UI.
   * Transforms raw history data into display-ready records with human-readable
   * labels and provider names fetched asynchronously.
   *
   * This method performs the following operations:
   * 1. Validates that history data exists
   * 2. Transforms history records with human-readable field labels
   * 3. Fetches provider names for all users who made changes (in parallel)
   * 4. Enriches records with provider display names
   *
   * @param {DeathInformation} deathVisit - The death information object containing history records
   * @returns {Observable<DeathHistoryRecord[]>} Observable that emits processed history records
   *                                               when all provider names have been fetched,
   *                                               or empty array if no history exists
   *
   * @example
   * service.processDeathVisitHistoryForDisplay(deathInfo).subscribe(records => {
   *   console.log(records); // All records with provider names populated
   *   // [{ changesMadeTo: "Date / Time of Death", changesMadeByName: "John Doe", ... }]
   * });
   */
  processDeathVisitHistoryForDisplay(
    deathVisit: DeathInformation
  ): Observable<DeathHistoryRecord[]> {
    if (!this.hasValidHistory(deathVisit)) {
      return of([]);
    }

    const records = this.transformHistoryRecords(deathVisit.history);
    const providerIds = this.extractUniqueProviderIds(deathVisit.history);

    if (providerIds.length === 0) {
      return of(records);
    }

    return this.fetchProviderNames(providerIds).pipe(
      map((providerMap) =>
        this.enrichRecordsWithProviderNames(records, providerMap)
      )
    );
  }

  /**
   * Transforms raw history records into display format with human-readable labels.
   * Converts API field names to user-friendly labels using the getDeathVisitLabel function.
   *
   * @param {HistoryRecord[]} history - Array of history records from the API
   * @returns {DeathHistoryRecord[]} Array of transformed death history records
   *
   * @example
   * // Input: [{ changesMadeTo: "datetimeOfDeath", createdBy: 123, ... }]
   * // Output: [{ changesMadeTo: "Date / Time of Death", changesMadeById: 123, ... }]
   */
  private transformHistoryRecords(
    history: HistoryRecord[]
  ): DeathHistoryRecord[] {
    return history.map((record) => ({
      changesMadeById: record.createdBy,
      changesMadeTo: getDeathVisitLabel(record.changesMadeTo),
      dateOfChange: record.createdDte,
      id: record.id,
      previousValue: record.previousValue,
      uuid: record.uuid,
    }));
  }

  /**
   * Extracts unique provider IDs from history records.
   * Uses a Set to ensure each provider ID appears only once, even if they
   * made multiple changes.
   *
   * @param {HistoryRecord[]} history - Array of history records
   * @returns {number[]} Array of unique provider IDs
   *
   * @example
   * // Input: [{ createdBy: 123 }, { createdBy: 456 }, { createdBy: 123 }]
   * // Output: [123, 456]
   */
  private extractUniqueProviderIds(history: HistoryRecord[]): number[] {
    const uniqueIds = new Set(history.map((record) => record.createdBy));
    return Array.from(uniqueIds);
  }

  /**
   * Fetches provider names for all given provider IDs in parallel.
   * Makes concurrent HTTP requests for each provider and includes error handling
   * and timeout protection for each individual request.
   *
   * If a provider fetch fails or times out, it gracefully falls back to a
   * default label instead of failing the entire operation.
   *
   * @param {number[]} providerIds - Array of provider IDs to fetch
   * @returns {Observable<Map<number, string>>} Observable that emits a Map of provider ID to display name
   *
   * @example
   * fetchProviderNames([123, 456]).subscribe(map => {
   *   console.log(map.get(123)); // "John Doe"
   *   console.log(map.get(456)); // "Jane Smith"
   * });
   */
  private fetchProviderNames(
    providerIds: number[]
  ): Observable<Map<number, string>> {
    const providerRequests = providerIds.map((id) =>
      this.resourceManagementService.getProviderByExternalId(id).pipe(
        timeout(this.PROVIDER_FETCH_TIMEOUT_MS),
        map((provider) => ({
          id,
          name: provider?.findDisplayName() ?? `Provider ${id}`,
        })),
        catchError((err) => {
          console.error(`Failed to fetch provider ${id}:`, err);
          return of({
            id,
            name: this.UNKNOWN_PROVIDER_LABEL,
          });
        })
      )
    );

    return forkJoin(providerRequests).pipe(
      map((providers) => new Map(providers.map((p) => [p.id, p.name])))
    );
  }

  /**
   * Enriches history records with provider display names.
   * Maps provider IDs to their corresponding display names and adds the
   * changesMadeByName property to each record.
   *
   * @param {DeathHistoryRecord[]} records - Array of death history records
   * @param {Map<number, string>} providerMap - Map of provider ID to display name
   * @returns {DeathHistoryRecord[]} Array of records with changesMadeByName populated
   *
   * @example
   * // Input records: [{ changesMadeById: 123, ... }]
   * // Input map: Map { 123 => "John Doe" }
   * // Output: [{ changesMadeById: 123, changesMadeByName: "John Doe", ... }]
   */
  private enrichRecordsWithProviderNames(
    records: DeathHistoryRecord[],
    providerMap: Map<number, string>
  ): DeathHistoryRecord[] {
    return records.map((record) => ({
      ...record,
      changesMadeByName:
        providerMap.get(record.changesMadeById) ?? this.UNKNOWN_PROVIDER_LABEL,
    }));
  }

  /**
   * Type guard to validate death visit has history records.
   * Performs comprehensive null/undefined checks and validates that the history
   * array exists and contains at least one record.
   *
   * This is a guard method used to prevent processing when no history data
   * is available, avoiding unnecessary operations and potential errors.
   *
   * @param {DeathInformation | null | undefined} deathVisit - The death information object to validate
   * @returns {boolean} True if death visit has valid history array with records
   *
   * @example
   * if (this.hasValidHistory(deathInfo)) {
   *   // TypeScript knows deathInfo.history is defined and has length > 0
   *   const firstRecord = deathInfo.history[0];
   * }
   */
  private hasValidHistory(
    deathVisit: DeathInformation | null | undefined
  ): deathVisit is DeathInformation & { history: HistoryRecord[] } {
    return Boolean(
      deathVisit?.history &&
        Array.isArray(deathVisit.history) &&
        deathVisit.history.length > 0
    );
  }
}



// WEBPACK FOOTER //
// ./src/providers/death-visit-history/death-visit-history.service.ts